/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import ChartInsightsTask from './rendersequence/ChartInsightsTask';
import ChartInsightsQuery from './ChartInsightsQuery';

/**
 * The ChartInsights feature is used to obtain chart-insights queries based on rendered results data.
 **/
class ChartInsights {
	constructor(widgetApi, dashboardApi) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;

		this._fnResultsHandler = this.buildChartInsightsQuerySpecsFromUnprocessedData.bind(this);
	}

	destroy() {
		const queryAPI = this.getQueryAPI();
		queryAPI && queryAPI.deregisterUpdateHandler && queryAPI.deregisterUpdateHandler(this._fnResultsHandler);
	}

	getAPI() {
		return {
			activate: (options) => this.activate(options),
			deactivate: () => this.deactivate(),
			getInsights: () => this.getInsights(),
			getChartInsightsCalculations: () => {
				if (this.chartInsightsQuery) {
					//this.chartInsightsQuery exists only if this.activate is called
					return this.chartInsightsQuery.getChartInsightsCalculationArray();
				}
			},
			//getNLTData() IS DEPRECATED (same as getChartInsightsCalculations): will be removed on next live widget update.
			getNLTData: (asCalcArray, annotations) => {
				if (this.chartInsightsQuery) {
					//this.chartInsightsQuery exists only if this.activate is called
					return this.chartInsightsQuery.getChartInsightsCalculationArray(annotations);
				}
			}
		};
	}

	activate(options = {}) {
		this.widgetContentApi = options.widgetContentApi;
		this.chartInsightsQuery = new ChartInsightsQuery(this.widgetContentApi, this.dashboardApi);
		this.cbChartInsightsUpdated = options.cbChartInsightsUpdated;
		this._addUnprocessedResultDataHandler(this._fnResultsHandler);
	}

	getQueryAPI() {
		if (this.chartInsightsQuery.useFPDAsInsightsInput()) {
			//If this is an "FPD-type" visualization (like Driver Analysis), use the keyDrivers feature as the queryAPI
			//for getting relevant query results.
			const keyDrivers = this.widgetContentApi && this.widgetContentApi.getFeature('VisKeyDrivers');
			if (keyDrivers && keyDrivers.isVisKeyDriversFeatureEnabled()) {
				return keyDrivers;
			}
		}

		return null;
	}

	_addUnprocessedResultDataHandler() {
		const queryAPI = this.getQueryAPI();
		return queryAPI && queryAPI.registerUpdateHandler && queryAPI.registerUpdateHandler(this._fnResultsHandler);
	}

	deactivate() {
		this.cbChartInsightsUpdated=undefined;
	}

	/**
	 * set data that will be the basis of a chartInsightsQuery for subsequent getInsights() calls.
	 * This function also notifies a registered caller that insights have changed
	 * (and the caller should call getInsights() to get the latest)
	 * NOTE: Generally, setChartInsightsData should be used to set the data for chartInsights queries
	 * (its very efficient for cases where data changes and you're not viewing the details panel)
	 * The exception is when we need to use un-postprocessed or FPD data (see below)
	 * @param chartInsightsData - the source data that needs to be used for the chartInsightsQuery.
	 */
	setChartInsightsData(chartInsightsData) {
		if (!this.chartInsightsQuery.useFPDAsInsightsInput()) {
			this.chartInsightsData=chartInsightsData;
			this.cbChartInsightsUpdated && this.cbChartInsightsUpdated();
		}
		this._addUnprocessedResultDataHandler(this.buildChartInsightsQuerySpecsFromUnprocessedData);
	}

	/**
	 * For certain cases (aka multi-measure), the results postProcessor makes drastic changes to the DSS query response.
	 * In this case, we need to use the preprocessed DSS response directly for chartInsights.
	 * We could use this technique all the time but, since data will be changed during post-processing, we need to
	 * take a copy (which is a bit more overhead).
	 * Since we need to copy the data anyway, we might as well create the queries right away.
	 * This function also notifies a registered caller that insights have changed
	 * @param chartInsightsData - the source data that needs to be used for the chartInsightsQuery.
	 * @param options For example, {type: 'FPD'}, used to customize the handler depending on needs
	 *														 {layer: 'layerId'} for DSS data.
	 */
	buildChartInsightsQuerySpecsFromUnprocessedData(chartInsightsData, options) {
		if(this.chartInsightsQuery.useFPDAsInsightsInput() && options && options.type === 'FPD'){
			this._setQuerySpecs(chartInsightsData);
			this.cbChartInsightsUpdated && this.cbChartInsightsUpdated();
		}
	}

	_setQuerySpecs(dataArray=[]){
		this._querySpecs = !Array.isArray(dataArray) ? [dataArray] : dataArray;
	}
	/**
	 * The main 'host-facing API' used to obtain chart-insights for a particular widget.
	 * @returns a promise which is resolved when the insights are available (possibly empty)
	 */
	getInsights() {
		const getInsightsResponse = (insights = []) => ({ insights });
		if (!this.chartInsightsQuery.chartInsightsQuerySupported()) {
			return Promise.resolve(getInsightsResponse());
		}

		const state = this.widgetContentApi.getFeature('state');
		return state.whenStatusChanges(state.STATUS['RENDERED']).then(() => {
			if (this.chartInsightsQuery.useFPDAsInsightsInput()) {
				return this.chartInsightsQuery.executeQuerySpecs(this._querySpecs);
			} else {
				return this.chartInsightsQuery.execute(this.chartInsightsData);
			}
		}).then((nltResponse) => {
			return getInsightsResponse(nltResponse);
		});
	}

	/**
	* Return the feature extra render sequence steps
	*/
	getExtraRenderSequenceSteps() {
		return [{
			id: 'ChartInsights',
			module: ChartInsightsTask,
			dependencies:['data'],
			moduleOptions: {
				chartInsightsFeature: this
			}
		}];
	}
}

export default ChartInsights;



// WEBPACK FOOTER //
// ./src/js/features/insights/ChartInsights.js